import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';

import { neutral } from '../../../styles/mui/colors';
import IconSortBy from '../../Icons/IconSortBy';
import Menu from '../../Menu/Menu';
import MenuContent from '../../Menu/MenuContent/MenuContent';
import MenuItem from '../../Menu/MenuItem/MenuItem';
import MenuLabel from '../../Menu/MenuLabel/MenuLabel';

const optionLabel = (options, key, intl) => {
  const option = options.find((o) => o.key === key);
  return option ? intl.formatMessage({ id: option.label }) : key;
};

const menuSx = {
  marginRight: 'auto',
};

const menuContentSx = {
  marginTop: '7px',
  paddingTop: 0,
  paddingBottom: '24px',
  minWidth: '300px',
  borderRadius: '4px',
};

const buttonSx = {
  borderRadius: 200,
  bgcolor: 'white',
  borderColor: neutral[200],
  '&:hover': {
    bgcolor: 'white',
  },
  '&:active': {
    bgcolor: 'white',
  },
  '&:focus': {
    bgcolor: 'white',
  },
  '& .MuiButton-startIcon': {
    color: 'inherit',
  },
  color: 'inherit',
};

// Marker class used so the menu item button can animate the left "border" span on hover.
const MENU_ITEM_BORDER_CLASS = 'SortByPopup-menuItemBorder';

const menuHeadingSx = (theme) => ({
  fontWeight: theme.tokens.fontWeightSemiBold,
  color: theme.tokens.matterColor,
  marginTop: '24px',
  marginLeft: '30px',
  marginRight: '30px',
});

const menuItemSx = (theme) => ({
  ...theme.mixins.marketplaceListingAttributeFontStyles,
  color: theme.tokens.matterColor,

  position: 'relative',
  minWidth: '300px',
  margin: 0,
  padding: '4px 30px',

  outline: 'none',
  textAlign: 'left',
  border: 'none',

  cursor: 'pointer',

  '&:focus, &:hover': {
    color: theme.tokens.matterColorDark,
  },

  [`&:hover .${MENU_ITEM_BORDER_CLASS}`]: {
    width: '6px',
  },

  '&:disabled': {
    color: theme.tokens.matterColorAnti,
    cursor: 'default',
  },

  [`&:disabled:hover .${MENU_ITEM_BORDER_CLASS}`]: {
    width: 0,
  },
});

// left animated "border" like hover element
const menuItemBorderSx = (theme) => ({
  position: 'absolute',
  top: '2px',
  left: 0,
  height: 'calc(100% - 4px)',
  width: 0,
  backgroundColor: theme.tokens.marketplaceColor,
  transition: `width ${theme.tokens.transitionStyleButton}`,
});

// left static border for selected element
const menuItemBorderSelectedSx = (theme) => ({
  position: 'absolute',
  top: '2px',
  left: 0,
  height: 'calc(100% - 7px)',
  width: '6px',
  backgroundColor: theme.tokens.matterColorDark,
});

/**
 * @param {object} props - Component props
 * @param {string} [props.rootClassName] - Root class name override
 * @param {string} [props.className] - Additional class name
 * @param {string} [props.menuLabelRootClassName] - Menu label root class name override
 * @param {string} props.label - Default menu label
 * @param {Array<{key: string, label: string, longLabel?: string, disabled?: boolean}>} props.options - Sort options
 * @param {string} [props.initialValue] - Currently selected option key
 * @param {string} props.urlParam - URL param name passed back to onSelect
 * @param {Function} props.onSelect - Called with (urlParam, optionKey) on selection
 * @param {number} [props.contentPlacementOffset] - Menu content placement offset
 * @param {boolean} props.annualPassIsValid - Whether the annual pass is valid (hides price options)
 * @param {boolean} props.isMobile - Whether the button renders in mobile size
 * @returns {React.ReactNode} - Component
 */
const SortByPopupComponent = ({
  urlParam,
  label,
  options,
  initialValue = null,
  contentPlacementOffset = 0,
  annualPassIsValid,
  isMobile,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();

  const selectOption = (param, option) => {
    setIsOpen(false);
    onSelect(param, option);
  };

  // resolve menu label text and class
  const menuLabel =
    initialValue && !annualPassIsValid
      ? optionLabel(options, initialValue, intl)
      : initialValue.includes('price') && annualPassIsValid
      ? label
      : initialValue && annualPassIsValid
      ? optionLabel(options, initialValue, intl)
      : label;

  return (
    <Menu
      contentPlacementOffset={contentPlacementOffset}
      isOpen={isOpen}
      onToggleActive={setIsOpen}
      sx={menuSx}
      useArrow={false}
    >
      <MenuLabel>
        <Button
          component="div"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<IconSortBy />}
          sx={buttonSx}
          variant="outlined"
        >
          <Typography component="span" whiteSpace="nowrap">
            {menuLabel}
          </Typography>
        </Button>
      </MenuLabel>

      <MenuContent sx={menuContentSx}>
        <MenuItem key="sort-by">
          <Box component="p" sx={menuHeadingSx}>
            {label}
          </Box>
        </MenuItem>
        {options.map((option) => {
          if (annualPassIsValid && option.key.includes('price')) return null;
          // check if this option is selected
          const selected = initialValue === option.key;
          // menu item border style: static for selected, animated otherwise.
          // Only the animated (non-selected) span carries the marker class the parent
          // button targets on hover — mirroring the original .menuItemBorder selector,
          // which never matched the distinct .menuItemBorderSelected class.
          const menuItemBorderStyle = selected ? menuItemBorderSelectedSx : menuItemBorderSx;

          return (
            <MenuItem key={option.key}>
              <Box
                component="button"
                disabled={option.disabled}
                onClick={() => (selected ? null : selectOption(urlParam, option.key))}
                sx={menuItemSx}
              >
                <Box
                  className={selected ? undefined : MENU_ITEM_BORDER_CLASS}
                  component="span"
                  sx={menuItemBorderStyle}
                />
                {intl.formatMessage({ id: option.longLabel || option.label })}
              </Box>
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
  );
};

const SortByPopup = memo(SortByPopupComponent);

export default SortByPopup;
